<template>
  <v-container class="insights">
    <template v-if="!loading.view && allFacilitiesIds.length === 0">
      <v-row>
        <ViewHeader :title="viewTitle"> </ViewHeader>
        <v-col cols="12">
          <v-card style="min-height: 650px;" class="d-flex">
            <div class="mx-auto d-flex align-center">
              <div class="no-data d-flex flex-column align-center">
                <h2 class="subtitle mb-6">
                  {{ $t("message.common.noData") }}
                </h2>
                <p class="no-data__description text-center">
                  {{ $t("message.analytics.businessAddresses.noData") }}
                </p>
                <v-btn color="secondary" @click="goToGMBAccounts">
                  {{ $t("message.common.addAccount") }}
                </v-btn>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>
    <template v-if="allFacilitiesIds.length > 0">
      <v-row justify="center" no-gutters>
        <v-col cols="12">
          <ViewHeader :title="viewTitle">
            <div class="header-actions">
              <div class="mr-4">
                <v-tooltip bottom :max-width="tooltipMaxWidth">
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      <v-icon :color="infoIconColor" size="17.5">
                        mdi-information
                      </v-icon>
                    </div>
                  </template>
                  {{ $t("message.analytics.businessAddresses.titleTooltip") }}
                </v-tooltip>
              </div>

              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                :return-value.sync="date"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :hide-details="true"
                    :value="dateRangeText"
                    :label="$t('message.common.chooseDate')"
                    prepend-icon="icon-calendar"
                    readonly
                    v-on="on"
                    class="ml-auto mr-8"
                    style="min-width: 230px;"
                  ></v-text-field>
                </template>

                <v-date-picker
                  ref="datepicker"
                  v-model="date"
                  first-day-of-week="1"
                  :locale="selectedLocale"
                  :allowed-dates="getAllowedDays"
                  no-title
                  scrollable
                  range
                  color="primary"
                  class="pa-2"
                >
                  <v-tooltip
                    v-model="tooltipVisible"
                    :position-x="tooltipPosition.left"
                    :position-y="tooltipPosition.top"
                    absolute
                    bottom
                    :max-width="tooltipMaxWidth"
                  >
                    <span>{{
                      $t("message.usersBehavior.lastDaysTooltip")
                    }}</span>
                  </v-tooltip>

                  <div class="full-width">
                    <div class="dates-select mb-0">
                      <v-text-field
                        v-model="dateFrom"
                        @change="setDates"
                        :label="$t('message.common.dateFrom')"
                      ></v-text-field
                      ><v-text-field
                        v-model="dateTo"
                        @change="setDates"
                        :label="$t('message.common.dateTo')"
                      ></v-text-field>
                    </div>
                    <v-tooltip bottom :max-width="tooltipMaxWidth">
                      <template v-slot:activator="{ on, attrs }">
                        <div
                          v-bind="attrs"
                          v-on="on"
                          class="dates-selected__info"
                        >
                          {{ $t("message.usersBehavior.lastDaysLabel") }}
                        </div>
                      </template>
                      <span>{{
                        $t("message.usersBehavior.lastDaysTooltip")
                      }}</span>
                    </v-tooltip>
                    <div class="compare-select">
                      <span class="mr-2 text-no-wrap mb-3">{{
                        $t("message.common.compareWith")
                      }}</span>
                      <v-select
                        :items="comparePeriodItems"
                        v-model="comparePeriodValue"
                        dense
                      ></v-select>
                    </div>
                    <div class="d-flex justify-space-between">
                      <ui-button outlined color="primary" @click="menu = false">
                        {{ $t("message.common.cancel") }}
                      </ui-button>
                      <v-spacer />
                      <ui-button color="primary" @click="handleDatePickerOk">
                        {{ $t("message.common.ok") }}
                      </ui-button>
                    </div>
                  </div>
                </v-date-picker>
              </v-menu>
              <div class="csv-export">
                <v-menu
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                >
                  <template v-slot:activator="{ on }">
                    <ui-button
                      v-on="on"
                      color="primary"
                      :loading="loading.exportBtn"
                      append-icon="icon-arrow-down"
                    >
                      {{ $t("message.common.exportData") }}
                    </ui-button>
                  </template>
                  <v-list class="csv-export__list">
                    <v-list-group>
                      <template v-slot:activator>
                        <v-list-item-title>{{
                          $t("message.common.exportGroupedByDates")
                        }}</v-list-item-title>
                      </template>
                      <v-list-item
                        class="csv-export__list-item"
                        @click="
                          exportGMBAddressesData(EXPORT_FILE_EXTENSION.CSV)
                        "
                      >
                        {{ $t("message.common.csv") }}
                      </v-list-item>
                      <v-list-item
                        class="csv-export__list-item"
                        @click="
                          exportGMBAddressesData(EXPORT_FILE_EXTENSION.XLSX)
                        "
                      >
                        {{ $t("message.common.xlsx") }}
                      </v-list-item>
                    </v-list-group>
                    <v-list-group>
                      <template v-slot:activator>
                        <v-list-item-title>{{
                          $t("message.common.exportGroupedByLocations")
                        }}</v-list-item-title>
                      </template>
                      <v-list-item
                        class="csv-export__list-item"
                        @click="
                          exportGMBAddressesDataAsync(
                            EXPORT_FILE_EXTENSION.CSV,
                            GMB_EXPORT_GROUP_BY.GMB_ADDRESS
                          )
                        "
                      >
                        {{ $t("message.common.csv") }}
                      </v-list-item>
                      <v-list-item
                        class="csv-export__list-item"
                        @click="
                          exportGMBAddressesDataAsync(
                            EXPORT_FILE_EXTENSION.XLSX,
                            GMB_EXPORT_GROUP_BY.GMB_ADDRESS
                          )
                        "
                      >
                        {{ $t("message.common.xlsx") }}
                      </v-list-item>
                    </v-list-group>
                  </v-list>
                </v-menu>
              </div>
              <ui-button
                color="primary"
                @click.native.stop="filterDialog = true"
                icon="icon-filter"
              >
                {{ $t("message.common.filter") }}
              </ui-button>
            </div>
          </ViewHeader>
        </v-col>
      </v-row>
      <v-divider />
      <v-row>
        <v-col cols="12">
          <div class="archive-page-alert">
            {{ $t("message.analytics.businessAddresses.archiveAlert1") }}
            <router-link
              class="archive-page-alert__link"
              :to="{ name: 'analytics-gmb-addresses-archive' }"
              >{{ $t("message.analytics.businessAddresses.here") }}</router-link
            >.
            <span
              class="ml-0.5"
              v-html="$t('message.analytics.businessAddresses.archiveAlert2')"
            ></span>
          </div>
        </v-col>
      </v-row>
      <div v-if="!loading.view">
        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col cols="12">
                <h3 class="subtitle mb-2 mt-6" id="viewsCount">
                  {{
                    $t("message.analytics.businessAddresses.subtitle.displays")
                  }}
                </h3>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-card
                  min-height="200"
                  class="fill-height pa-8"
                  v-if="charts && charts.searchMaps.sum !== '0'"
                >
                  <div class="d-flex mb-4">
                    <h4 class="chart-title">
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.searchMaps.title.text"
                        )
                      }}
                    </h4>
                    <ChartTitleTooltip
                      :tooltip="
                        $t(
                          'message.analytics.businessAddresses.chart.searchMaps.title.tooltip'
                        )
                      "
                      :tooltipMaxWidth="tooltipMaxWidth"
                      :infoIconColor="infoIconColor"
                    />
                  </div>
                  <div class="pa-0 pa-lg-2 pa-xl-8">
                    <DoughnutChart
                      :data="charts.searchMaps.data"
                      :labels="charts.searchMaps.labels"
                      :options="charts.searchMaps.options"
                      :colors="charts.searchMaps.colors"
                    />
                    <div class="labels">
                      <div
                        v-for="(label, index) in charts.searchMaps.labels"
                        :key="`label-${index}`"
                        class="label"
                      >
                        <span
                          class="label-color"
                          :style="
                            `backgroundColor: ${charts.searchMaps.colors[index]}`
                          "
                        ></span>
                        <span class="label-text">{{ label }}</span>
                      </div>
                    </div>
                  </div>
                </v-card>
                <v-card
                  min-height="200"
                  class="d-flex justify-center align-center fill-height pa-8"
                  v-else
                >
                  <no-data
                    :description="
                      isDateInRange
                        ? $t('message.common.noDataDescription')
                        : ''
                    "
                  ></no-data>
                </v-card>
              </v-col>
              <v-col cols="8">
                <v-card
                  min-height="200"
                  class="fill-height pa-8"
                  v-if="charts && charts.maps.sum !== '0'"
                >
                  <div class="d-flex mb-4">
                    <h4 class="chart-title">
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.maps.title.text"
                        )
                      }}
                    </h4>
                    <ChartTitleTooltip
                      :tooltip="
                        $t(
                          'message.analytics.businessAddresses.chart.maps.title.tooltip'
                        )
                      "
                      :tooltipMaxWidth="tooltipMaxWidth"
                      :infoIconColor="infoIconColor"
                    />
                    <v-spacer />
                    <span>{{ charts.maps.sum }}</span>
                    <LastAudit
                      v-if="charts.maps.percentage !== null"
                      class="ml-3"
                      hideTitle
                      showZero
                      :lastAudit="charts.maps.percentage"
                    />
                  </div>
                  <BarChart
                    :data="charts.maps.data"
                    :labels="charts.maps.dataSetsLabels"
                    :options="charts.maps.options"
                  />
                  <div class="labels">
                    <div
                      v-for="(label,
                      index) in charts.maps.labels.slice().reverse()"
                      :key="`label-${index}`"
                      class="label"
                    >
                      <span
                        class="label-color"
                        :style="
                          `backgroundColor: ${
                            charts.maps.colors[
                              charts.maps.labels.length - 1 - index
                            ]
                          }`
                        "
                      ></span>
                      <span class="label-text">{{ label.text }}</span>
                    </div>
                  </div>
                </v-card>
                <v-card
                  min-height="200"
                  class="d-flex justify-center align-center fill-height pa-8"
                  v-else
                >
                  <no-data
                    :description="
                      isDateInRange
                        ? $t('message.common.noDataDescription')
                        : ''
                    "
                  ></no-data>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col cols="12">
                <h3 class="subtitle mb-0" id="actions">
                  {{
                    $t("message.analytics.businessAddresses.subtitle.actions")
                  }}
                </h3>
                <InfoBox
                  transparent
                  :text="
                    $t(
                      'message.analytics.businessAddresses.dataCollectInfoText'
                    )
                  "
                />
              </v-col>
            </v-row>
            <v-row
              v-if="isFeatureFlagEnabled(FEATURE_FLAG.SHOW_CONVERSION_CHARTS)"
            >
              <v-col cols="12">
                <div class="chart-switch">
                  <ui-switch v-model="showConversion" />
                  <span
                    class="chart-switch__label"
                    :class="{ 'chart-switch__label--active': showConversion }"
                  >
                    {{
                      $t(
                        "message.analytics.businessAddresses.showConversionRate"
                      )
                    }}
                  </span>
                </div>
              </v-col>
            </v-row>
            <ConversionCharts
              v-if="showConversion"
              :charts="charts"
              :infoIconColor="infoIconColor"
              :tooltipMaxWidth="tooltipMaxWidth"
              :is-date-in-range="isDateInRange"
            />
            <AbsoluteCharts
              v-else
              :charts="charts"
              :infoIconColor="infoIconColor"
              :tooltipMaxWidth="tooltipMaxWidth"
              :is-date-in-range="isDateInRange"
            />
          </v-col>

          <FilterDialog
            v-if="filterDialog"
            :gmb-addresses-ids="gmbAddressesIds"
            :cities="cities"
            :facilities-groups="facilitiesGroupsIds"
            :chosen-search-type="searchType"
            @setGmbAddressesIds="gmbAddressesIds = $event"
            @setCities="cities = $event"
            @setFacilitiesGroupsIds="facilitiesGroupsIds = $event"
            @setSearchType="searchType = $event"
            @change="onFiltersChange({ ...$event })"
            :handleClose="closeFilterDialog"
          />
        </v-row>
      </div>
    </template>
    <SectionLoader :status="loading.view" :opacity="1" />
    <AsyncDownloadDialog
      v-if="showAsyncDownloadDialog"
      :export-id="exportId"
      @close="showAsyncDownloadDialog = false"
    />
  </v-container>
</template>

<script>
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { scrollToHash } from "@/helpers/mixins/scroll-to-hash";
import { gmbAddressMixin } from "@/helpers/mixins/gmb-address";
import { GMBAddressesFacadeUpdated as GMBAddressesFacade } from "@/utils/google/GMB";
import moment from "moment";
import { FORMAT } from "@/config/date-format";
import { PERIOD } from "@/enums/period.enum";
import InfoBox from "@/components/ui/InfoBox";
import debounce from "lodash/debounce";
export default {
  name: "GMBAnalyticsView",
  components: {
    InfoBox,
    AsyncDownloadDialog: () => import("@/components/AsyncDownloadDialog")
  },
  mixins: [scrollToHash, gtmEngagementMixin, gmbAddressMixin],
  data() {
    return {
      showAsyncDownloadDialog: false,
      dateFrom: moment()
        .subtract(1, PERIOD.MONTH)
        .subtract(14, "d")
        .format(FORMAT.DATE),
      dateTo: moment()
        .subtract(14, "d")
        .format(FORMAT.DATE),
      date: null,
      tooltipVisible: false,
      tooltipPosition: {
        left: 0,
        top: 0
      }
    };
  },

  created() {
    this.date = [this.dateFrom, this.dateTo];
    this.gmbAddressesFacade = new GMBAddressesFacade();
  },
  methods: {
    shouldShowTooltip(event) {
      const isDisabledButton =
        event.target.tagName === "TD" &&
        event.target.querySelector(".v-btn--disabled");
      const currentElement = document.querySelector(
        ".v-date-picker-table__current"
      );
      const isBeforeOrEqualCurrent =
        currentElement &&
        (event.target === currentElement.parentNode ||
          event.target.compareDocumentPosition(currentElement.parentNode) &
            Node.DOCUMENT_POSITION_FOLLOWING);

      return (
        (isDisabledButton && isBeforeOrEqualCurrent) ||
        (isDisabledButton && !currentElement)
      );
    },
    getTooltipPosition(event) {
      const { top, left } = event.target.getBoundingClientRect();
      return {
        top,
        left
      };
    },
    handleMouseOver: debounce(function(event) {
      if (this.shouldShowTooltip(event)) {
        this.tooltipVisible = true;
        this.tooltipPosition = this.getTooltipPosition(event);
      }
    }, 100),
    hideTooltip() {
      this.tooltipVisible = false;
    },
    getAllowedDays(val) {
      const dateFrom = moment()
        .subtract(13, "d")
        .format(FORMAT.DATE);
      const dateTo = moment().format(FORMAT.DATE);
      const compareDate = moment(val);

      return (
        !compareDate.isBetween(dateFrom, dateTo, "days", "[]") &&
        !compareDate.isAfter(dateTo, "days")
      );
    },
    removeListeners() {
      if (!this.$refs.datepicker) return;
      const datePickerTable = this.$refs.datepicker.$el.querySelector(
        ".v-date-picker-table"
      );
      if (!datePickerTable) {
        return;
      }
      datePickerTable.removeEventListener("mouseover", this.handleMouseOver);
      datePickerTable.removeEventListener("mouseout", this.hideTooltip);
    }
  },
  watch: {
    async menu(current) {
      if (current) {
        await this.$nextTick();
        const datePickerTable = this.$refs.datepicker.$el.querySelector(
          ".v-date-picker-table"
        );

        datePickerTable.addEventListener("mouseover", this.handleMouseOver);
        datePickerTable.addEventListener("mouseout", this.hideTooltip);
      } else {
        this.removeListeners();
      }
    }
  },
  beforeDestroy() {
    this.removeListeners();
  }
};
</script>

<style lang="scss" scoped src="./GMBAddress.scss"></style>
<style scoped lang="scss">
.dates-selected {
  &__info {
    cursor: pointer;
    @include font-size(12px);
    text-decoration: underline;
    margin-bottom: $spacing;

    &:hover {
      text-decoration: none;
    }
  }
}
</style>
